<template>
  <div v-if="!loading" :class="classList" class="package-selector">
    <searchable-package-list
      v-if="showSearchList"
      :value="selectablePackages"
      :query="query"
      class="search-list"
      @query-change="handleChangeQuery"
      @select="handleSelectPackage"
      @end="handleEnd"
      @addAllPackages="handleSelectAllPackages"
    ></searchable-package-list>
    <selected-package-list
      v-model="selectedPackages"
      :disabled="disabled"
      :selected-package-label="selectedPackageLabel"
      :is-action-edit="isActionEdit"
      class="selected-list"
      @remove="handleRemovePackage"
      @removeAll="handleRemoveAllPackage"
    ></selected-package-list>
  </div>
</template>

<script>
import differenceWith from "ramda/src/differenceWith";
import { MessageBox } from "element-ui";
import { baanRueJaiClient } from "@/dependencies";
import { debounce } from "@/lib/util";
import SelectedPackageList from "./SelectedPackageList";
import SearchablePackageList from "./SearchablePackageList";

export default {
  components: {
    SelectedPackageList,
    SearchablePackageList
  },
  props: {
    initialPackages: { type: Object, default: null },
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    selectedLabel: { type: String, default: "" },
    isAllTypePackage: { type: Boolean, default: false },
    isActionEdit: { type: Boolean, default: false },
    excludedPackages: { type: Array, default: null },
    isCouponPage: { type: Boolean, default: false }
  },
  data() {
    // excludedPacks มีในกรณีเป็น coupon page
    const excludedPacks = this.excludedPackages || null

    return {
      query: "",
      allPackages: [],
      loading: true,
      page: 1,
      excludedPacks
    };
  },
  computed: {
    selectedPackages: {
      get() {
        const selectedPackagesSort = [...this.value].sort((a, b) => a.hasSelected - b.hasSelected);

        return selectedPackagesSort;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    selectablePackages() {
      const selectablePack = differenceWith(
        this.comparePackage,
        this.allPackages,
        this.value
      );

      return selectablePack;
    },
    disabledRemoveButton() {
      return this.value.length === 0;
    },
    showSearchList() {
      return !this.disabled;
    },
    classList() {
      return { disabled: this.disabled };
    },
    selectedPackageLabel() {
      const label = this.selectedLabel ? this.selectedLabel : "เลือกแล้ว";

      return label;
    }
  },
  watch: {
    query() {
      this.watchQuery();
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    async setupData() {
      this.loading = true;
      await this.setupPackages();
      this.loading = false;
    },
    async setupPackages() {
      if (this.excludedPacks && this.excludedPacks.length > 0) {
        // ในกรณีเป็น excludedPacks มาจาก couponEditor
        await this.fetchPackageCoupon();
      } else {
         await this.fetchNormalPackageCoupon();
      }
    },
    async fetchPackageCoupon() {
      if (!this.query) {
        this.allPackages = this.excludedPacks;
      } else {
        this.allPackages = this.excludedPacks.filter(
          (excludedPack) => excludedPack.name.includes(this.query)
        );
      }
    },
    async fetchNormalPackageCoupon() {
      const { packages } = await this.fetchPackages();

      this.allPackages = packages.map(item => {
        const {
          id,
          name,
          subpackages,
          price,
          billing_cycle: billingCycle,
          target,
          detail,
          service_ids: serviceIds,
          project_ids: projectIds,
          image_url: imageUrl,
          original_price: originalPrice
        } = item;

        return {
          id: id.toString(),
          name,
          subpackages,
          target,
          price,
          billingCycle,
          detail,
          serviceCount: serviceIds.length,
          projectCount: (projectIds || []).length,
          imageUrl,
          originalPrice
        };
      });
    },
    async fetchPackages() {
      if (this.initialPackages) {
        return this.initialPackages;
      }

      const name = this.query;
      const {page} = this;

      const params = {
        page,
        name,
        nosubpack: 1
      } ;

      if (this.isCouponPage && !this.isAllTypePackage) {
        params.isCouponPage = true;
      }

      const response = this.isAllTypePackage
        ? await baanRueJaiClient.admin.subscriptions.recommend_packages.listAllType(params)
        : await baanRueJaiClient.admin.subscriptions.recommend_packages.list(params);

      return response.data.result;
    },
    handleSelectPackage(item) {
      if (this.excludedPacks && this.excludedPacks.length > 0) {
        this.excludedPacks = this.excludedPacks.filter((i) => i.id !== item.id)
      }

      this.updateInput([...this.value, item]);
    },
    handleRemovePackage(item) {
      this.updateInput(this.value.filter(({ id }) => item.id !== id));
    },
    handleChangeQuery(query) {
      this.query = query;
    },
    async handleRemoveAllPackage() {
      try {
        await MessageBox.confirm("คุณต้องการลบรายการแพ็กเกจใช่หรือไม่", { confirmButtonText: "ตกลง", cancelButtonText: "ยกเลิก" });
      } catch { return; }

      this.updateInput([]);
    },
    async handleEnd($state) {
      // excludedPacks mode: allPackages is the complete preloaded list,
      // not paginated. Short-circuit to stop infinite-loading from firing.
      if (this.excludedPacks !== null) {
        $state.complete();

        return;
      }

      this.page += 1;

      const { packages } = await this.fetchPackages();

      if (!packages.length) {
        $state.complete();
        return;
      }

      const newPackages = packages.map(item => {
        const {
          id,
          name,
          subpackages,
          price,
          billing_cycle: billingCycle,
          detail,
          service_ids: serviceIds,
          project_ids: projectIds,
          image_url: imageUrl
        } = item;

        return {
          id: id.toString(),
          name,
          subpackages,
          target: "สำหรับคอนโด",
          price,
          billingCycle,
          detail,
          serviceCount: serviceIds.length,
          projectCount: (projectIds || []).length,
          imageUrl,
          hasSelected: false
        };
      });

      this.allPackages = [...this.allPackages, ...newPackages];
      $state.loaded();
    },
    watchQuery: debounce(function watchQuery() {
      this.page = 1;

      this.setupPackages();
    }, 1.5),
    updateInput(value) {
      this.$emit("input", value);
    },
    comparePackage(item, package2) {
      return item.id === package2.id;
    },
    async handleSelectAllPackages() {
      const response = await this.getAllPackages();
      this.$emit("input", response);
    },
    async getAllPackages() {
      const { packages } = await this.fetchAllPackages();

      const allPackages = packages.map(item => {
        const {
          id,
          name,
          subpackages,
          price,
          billing_cycle: billingCycle,
          target,
          detail,
          service_ids: serviceIds,
          project_ids: projectIds,
          image_url: imageUrl,
          original_price: originalPrice
        } = item;

        return {
          id: id.toString(),
          name,
          subpackages,
          target,
          price,
          billingCycle,
          detail,
          serviceCount: serviceIds.length,
          projectCount: (projectIds || []).length,
          imageUrl,
          originalPrice
        };
      });

      return allPackages
    },
    async fetchAllPackages() {
      const response = await baanRueJaiClient.admin.subscriptions.recommend_packages.getAllPackages();

      return response.data.result
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.package-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;

  > .search-list {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  > .selected-list {
    border-left-width: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.package-selector.disabled {
  display: block;

  > .selected-list {
    border-left-width: 1px;
    border-radius: $border-radius;
  }
}
</style>
